import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import TopBar from "@/components/TopBar";
import api, { formatApiError } from "@/lib/api";
import { useConfirm } from "@/components/ConfirmDialog";
import { fmtNum, fmtRiyal } from "@/utils/toEnglishDigits";
import { Car as CarIcon, WhatsappLogo, Plus, Pencil, Trash, Phone, Path, Info } from "@phosphor-icons/react";
import { formatPhoneDisplay } from "@/utils/phoneFormat";
import PhoneInput from "@/components/PhoneInput";
import MultiPhoneInput, { hydratePhones, serializePhones, validatePhonesList } from "@/components/MultiPhoneInput";
import NumberField from "@/components/NumberField";

const emptyForm = () => ({
  name: "",
  phones: [{ number: "+966", is_primary: true }],
  car_type: null,      // Hidden from UI — kept as null (legacy field).
  fixed_wage: "",      // Optional. "" means "no fixed wage — use rules instead".
  notes: "",
  is_active: true,
  base_city: "",       // Required — "makkah" or "madinah".
});

function ExtModal({ open, initial, cars, onClose, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial?.id;

  useEffect(() => {
    if (open) {
      setForm(
        initial
          ? {
              name: initial.name || "",
              phones: hydratePhones(initial.phones, initial.phone),
              car_type: initial.car_type || null,
              fixed_wage: initial.fixed_wage != null ? initial.fixed_wage : "",
              notes: initial.notes || "",
              is_active: initial.is_active !== false,
              base_city: initial.base_city || "",
            }
          : emptyForm()
      );
    }
  }, [open, initial]);

  if (!open) return null;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error("الاسم مطلوب");
    const phoneError = validatePhonesList(form.phones);
    if (phoneError) return toast.error(phoneError);
    const phones = serializePhones(form.phones);
    if (phones.length === 0) return toast.error("رقم الجوال الأساسي مطلوب");
    if (!form.base_city) return toast.error("اختر مدينة الأساس (مكة أو المدينة)");
    setSaving(true);
    try {
      const primary = phones.find((p) => p.is_primary)?.number || phones[0].number;
      const payload = {
        ...form,
        phones,
        phone: primary,
        base_city: form.base_city,
        car_type: null,
        fixed_wage: form.fixed_wage === "" || form.fixed_wage == null ? 0 : Number(form.fixed_wage) || 0,
        is_active: !!form.is_active,
      };
      if (isEdit) {
        await api.patch(`/admin/external-drivers/${initial.id}`, payload);
        toast.success("تم التحديث");
      } else {
        await api.post("/admin/external-drivers", payload);
        toast.success("تم إضافة السائق الخارجي");
      }
      onSaved?.();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: "rgba(5,10,21,0.85)", backdropFilter: "blur(8px)" }}
      onClick={onClose}
      dir="rtl"
      data-testid="ext-driver-modal"
    >
      <div className="bg-card border border-primary/25 rounded-2xl w-full max-w-lg gold-glow" onClick={(e) => e.stopPropagation()}>
        <div className="p-5 border-b border-primary/15 flex items-center justify-between">
          <h3 className="text-primary font-bold text-lg">{isEdit ? "تعديل سائق خارجي" : "سائق خارجي جديد"}</h3>
          <button onClick={onClose} className="text-white/50 hover:text-white" data-testid="ext-modal-close">×</button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-3">
          <label className="block">
            <div className="text-xs text-white/60 mb-1 text-right">الاسم *</div>
            <input required value={form.name} onChange={(e) => set("name", e.target.value)} className="mz-input" data-testid="ext-name" />
          </label>
          <div>
            <div className="text-xs text-white/60 mb-1 text-right">أرقام التواصل *</div>
            <MultiPhoneInput
              value={form.phones}
              onChange={(v) => set("phones", v)}
              testIdPrefix="ext-phones"
              required
              maxEntries={5}
            />
          </div>
          <div>
            <div className="text-xs text-white/60 mb-2 text-right">مدينة الأساس <span className="text-red-400">*</span></div>
            <div className="grid grid-cols-2 gap-3">
              {[
                { code: "makkah", label: "مكة المكرمة", emoji: "🕌" },
                { code: "madinah", label: "المدينة المنورة", emoji: "🌙" },
              ].map((c) => {
                const active = form.base_city === c.code;
                return (
                  <button
                    key={c.code}
                    type="button"
                    onClick={() => set("base_city", c.code)}
                    data-testid={`ext-base-city-${c.code}`}
                    className={`p-4 rounded-xl border transition-all flex flex-col items-center justify-center gap-1.5 ${
                      active
                        ? "bg-primary text-primary-foreground border-primary font-black shadow-[0_4px_16px_rgba(201,169,97,0.35)]"
                        : "bg-primary/5 border-primary/25 text-white hover:bg-primary/15"
                    }`}
                  >
                    <span className="text-2xl leading-none">{c.emoji}</span>
                    <span className="text-sm">{c.label}</span>
                  </button>
                );
              })}
            </div>
          </div>
          <div className="p-3 rounded-lg bg-primary/[0.04] border border-primary/15 text-primary/90 text-xs flex items-start gap-2">
            <Info size={14} weight="fill" className="text-primary flex-shrink-0 mt-0.5" />
            <span>السائق الخارجي متاح لجميع أنواع السيارات — لا حاجة لتحديد نوع سيارة.</span>
          </div>
          <label className="block">
            <div className="text-xs text-white/60 mb-1 text-right">الأجرة الثابتة (ر.س) <span className="text-white/40">— اختياري</span></div>
            <NumberField min={0} value={form.fixed_wage} onChange={(val) => set("fixed_wage", val === "" ? "" : Number(val))} className="mz-input" testId="ext-wage" placeholder="0 (اتركه فارغاً لاستخدام قواعد الأجرة)" />
          </label>
          <label className="block">
            <div className="text-xs text-white/60 mb-1 text-right">ملاحظات</div>
            <textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} className="mz-input min-h-16" data-testid="ext-notes" />
          </label>
          <label className="flex items-center justify-between p-3 rounded-lg border border-primary/20 bg-primary/5 cursor-pointer">
            <span className="text-sm text-white">مفعّل</span>
            <input
              type="checkbox"
              checked={!!form.is_active}
              onChange={(e) => set("is_active", e.target.checked)}
              className="w-5 h-5 accent-primary"
              data-testid="ext-active"
            />
          </label>
          <div className="flex gap-3 pt-3">
            <button type="submit" disabled={saving} className="flex-1 py-3 rounded-xl bg-primary hover:bg-[#E1C484] text-primary-foreground font-bold disabled:opacity-60" data-testid="ext-save">
              {saving ? "جارٍ الحفظ…" : (isEdit ? "حفظ" : "إضافة")}
            </button>
            <button type="button" onClick={onClose} className="px-6 py-3 rounded-xl border border-white/20 text-white/70">إلغاء</button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ExternalDrivers() {
  const navigate = useNavigate();
  const [list, setList] = useState([]);
  const [cars, setCars] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const confirm = useConfirm();

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [e, c] = await Promise.all([api.get("/admin/external-drivers"), api.get("/cars")]);
      setList(e.data);
      setCars(c.data);
    } catch {
      toast.error("تعذر التحميل");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const remove = async (d) => {
    const ok = await confirm({
      title: "حذف سائق خارجي",
      message: `سيتم حذف السائق الخارجي "${d.name}" نهائياً.`,
      confirmText: "نعم، احذف",
      tone: "danger",
    });
    if (!ok) return;
    try {
      await api.delete(`/admin/external-drivers/${d.id}`);
      toast.success("تم الحذف");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const toggleActive = async (d) => {
    try {
      await api.patch(`/admin/external-drivers/${d.id}`, { is_active: !d.is_active });
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const carName = (id) => cars.find((c) => c.id === id)?.name_ar || "—";
  const waLink = (phone, name) => {
    const p = String(phone || "").replace(/\D/g, "");
    return `https://wa.me/${p}?text=${encodeURIComponent(`السلام عليكم ${name || ""}`)}`;
  };

  return (
    <div className="min-h-screen bg-background text-foreground" dir="rtl">
      <TopBar />
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-5 gap-3 flex-wrap">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-white">السائقون الخارجيون</h1>
            <p className="text-white/50 text-sm mt-1 flex items-center gap-1">
              <Info size={11} weight="fill" className="text-primary/70" />
              متاح لجميع أنواع السيارات · <span className="num">{fmtNum(list.length)}</span> سائق
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate("/admin/external-driver-wage-rules")}
              data-testid="ext-drivers-open-wage-rules"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl border border-primary/40 text-primary text-xs font-bold hover:bg-primary/10"
            >
              <Path size={14} weight="duotone" /> مسارات الأجرة
            </button>
            <button
              onClick={() => { setEditing(null); setModalOpen(true); }}
              data-testid="add-ext-driver"
              className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary hover:bg-[#E1C484] text-primary-foreground rounded-xl font-bold"
            >
              <Plus size={18} weight="bold" /> سائق خارجي
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-16 text-primary animate-pulse">جارٍ التحميل…</div>
        ) : list.length === 0 ? (
          <div className="text-center py-16 text-white/50 bg-card border border-primary/15 rounded-2xl">لا يوجد سائقون خارجيون بعد</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {list.map((d, i) => (
              <div
                key={d.id}
                data-testid={`ext-${d.id}`}
                className="fadeup bg-card border border-primary/15 rounded-2xl p-5 hover:border-primary/40 transition-all"
                style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}
              >
                <div className="flex items-start gap-3">
                  <div className="w-14 h-14 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center flex-shrink-0">
                    <CarIcon size={26} weight="duotone" className="text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-white font-bold truncate flex items-center gap-2">
                      <span className="truncate">{d.name}</span>
                      {d.base_city && (
                        <span
                          className="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-primary/15 border border-primary/40 text-primary flex-shrink-0"
                          data-testid={`ext-driver-city-${d.id}`}
                        >
                          {d.base_city === "makkah" ? "🕌 مكة" : "🌙 المدينة"}
                        </span>
                      )}
                      <button
                        type="button"
                        onClick={() => toggleActive(d)}
                        title={d.is_active ? "متوقّف" : "تفعيل"}
                        data-testid={`ext-toggle-${d.id}`}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold border flex-shrink-0 ${
                          d.is_active
                            ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-300"
                            : "bg-white/5 border-white/15 text-white/50"
                        }`}
                      >
                        {d.is_active ? "مفعّل" : "متوقّف"}
                      </button>
                    </div>
                    <div className="mt-1 space-y-1">
                      {(d.phones && d.phones.length > 0
                        ? d.phones
                        : d.phone ? [{ number: d.phone, is_primary: true }] : []
                      ).map((p, pIdx) => {
                        const digits = String(p.number || "").replace(/\D+/g, "");
                        if (!digits) return null;
                        return (
                          <div key={pIdx} className="flex items-center gap-1.5 flex-wrap" dir="ltr">
                            <span className="text-white/80 text-sm num">
                              {formatPhoneDisplay(p.number)}
                            </span>
                            {p.is_primary && (
                              <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-primary/20 border border-primary/40 text-primary font-bold">★</span>
                            )}
                            <a
                              href={`tel:${p.number}`}
                              title="اتصال"
                              data-testid={`ext-phone-call-${d.id}-${pIdx}`}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/15 hover:bg-primary/25 border border-primary/40 text-primary"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <Phone size={12} weight="fill" />
                            </a>
                            <a
                              href={`https://wa.me/${digits}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              title="واتساب"
                              data-testid={`ext-phone-wa-${d.id}-${pIdx}`}
                              className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#25D366] hover:bg-[#20BA5A]"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <WhatsappLogo size={12} weight="fill" className="text-white" />
                            </a>
                          </div>
                        );
                      })}
                    </div>
                    {/* car_type hidden — external drivers work for all car types */}
                    {Number(d.fixed_wage || 0) > 0 && (
                      <div className="text-primary font-bold text-sm mt-2 num">أجرة ثابتة: {fmtRiyal(d.fixed_wage)}</div>
                    )}
                    {d.notes && <div className="text-white/40 text-xs mt-1 truncate">{d.notes}</div>}
                  </div>
                </div>

                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-primary/10">
                  <button onClick={() => { setEditing(d); setModalOpen(true); }} className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-2 rounded-lg border border-primary/30 text-primary hover:bg-primary/10 text-xs font-semibold" data-testid={`edit-ext-${d.id}`}>
                    <Pencil size={13} weight="bold" /> تعديل
                  </button>
                  <button onClick={() => remove(d)} className="p-2 rounded-lg border border-destructive/50 text-destructive hover:bg-destructive/10" data-testid={`delete-ext-${d.id}`}>
                    <Trash size={14} weight="bold" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </main>

      <ExtModal open={modalOpen} initial={editing} cars={cars} onClose={() => setModalOpen(false)} onSaved={load} />
    </div>
  );
}
